/**
 * Model Compatibility Validation
 *
 * Guards against searching or incrementally indexing stores with mismatched embedding models.
 * Ensures stores are only queried with the same model they were indexed with.
 */

import type { Store } from '../types/store.js';

export interface ModelValidationContext {
  currentModelId: string;
}

/**
 * Validates that a store's embedding model matches the current configuration.
 *
 * @throws Error if store was indexed with a different model or has no model tracking
 */
export function validateStoreModelCompatibility(store: Store, ctx: ModelValidationContext): void {
  // Stores without modelId (schemaVersion < 2) must be reindexed
  if (!store.modelId) {
    throw new Error(
      `Store "${store.name}" has no model tracking (schema v1). ` +
        `Reindex required: /bluera-knowledge:index ${store.name}`
    );
  }

  if (store.modelId !== ctx.currentModelId) {
    throw new Error(
      `Model mismatch: Store "${store.name}" was indexed with "${store.modelId}", ` +
        `but current config uses "${ctx.currentModelId}". ` +
        `Reindex required: /bluera-knowledge:index ${store.name}`
    );
  }
}

/**
 * Check if a store's model matches the current configuration without throwing.
 *
 * @returns Object with compatibility status and details
 */
export function checkStoreModelCompatibility(
  store: Store,
  ctx: ModelValidationContext
): {
  compatible: boolean;
  modelId: string | undefined;
  reason?: string;
} {
  if (!store.modelId) {
    return {
      compatible: false,
      modelId: undefined,
      reason: 'Store has no model tracking (schema v1)',
    };
  }

  if (store.modelId !== ctx.currentModelId) {
    return {
      compatible: false,
      modelId: store.modelId,
      reason: `Indexed with different model: ${store.modelId}`,
    };
  }

  return {
    compatible: true,
    modelId: store.modelId,
  };
}
